import { forwardRef } from "react";

import { cn } from "~/lib/ui";

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAoMBgDTD2qgAAAAASUVORK5CYII=";

const getImageUrl = (
  imageUrl: string,
  width: number,
  quality: number = 85,
  resolution: number = 1,
): string => {
  if (!imageUrl) return TRANSPARENT_PIXEL;

  try {
    const url = new URL(
      imageUrl.startsWith("//") ? "https:" + imageUrl : imageUrl,
    );

    url.searchParams.set(
      "w",
      Math.min(width * resolution, MAX_IMAGE_DIMENSION).toString(),
    );
    url.searchParams.set("fm", "webp");
    url.searchParams.set("q", quality.toString());

    return url.toString();
  } catch (error) {
    console.error("Error generating image URL:", error);
    return TRANSPARENT_PIXEL;
  }
};

export const getImageMediaLink = (
  imageUrl: string,
  {
    quality,
    params,
    resolutions = [1],
  }: {
    quality?: number;
    params: Record<string, string>;
    resolutions?: number[];
  },
) => {
  if (!imageUrl) return TRANSPARENT_PIXEL;

  try {
    const url = new URL(
      imageUrl.startsWith("//") ? "https:" + imageUrl : imageUrl,
    );

    let searchParams = url.searchParams;

    if (params) {
      searchParams = new URLSearchParams({
        ...Object.fromEntries(searchParams),
        ...params,
      });
    }

    const srcSet = resolutions
      .map(resolution => {
        const scaledParams = new URLSearchParams(searchParams);

        const adjustedQuality = resolution > 1 ? 75 : quality;

        if (adjustedQuality) {
          scaledParams.set("q", adjustedQuality.toString());
        }

        if (scaledParams.has("w")) {
          const width = parseInt(scaledParams.get("w") || "0", 10);
          const calculatedWidth = width * resolution;

          // Cap the width if it exceeds MAX_IMAGE_DIMENSION
          const finalWidth = Math.min(calculatedWidth, MAX_IMAGE_DIMENSION);

          // Validate finalWidth
          if (isNaN(finalWidth) || finalWidth <= 0) {
            console.warn(
              `Invalid finalWidth: ${finalWidth}. Falling back to default width.`,
            );
            scaledParams.set("w", "300");
          } else {
            scaledParams.set("w", finalWidth.toString());
          }
        }

        return `${url.origin + url.pathname}?${scaledParams.toString()}${
          resolution > 1 ? ` ${resolution}x` : ""
        }`;
      })
      .join(", ");

    return srcSet;
  } catch (error) {
    console.error("Error generating image media link:", error);
    return TRANSPARENT_PIXEL;
  }
};

const MAX_IMAGE_DIMENSION = 4000;

const calculateImageWidth = (
  breakpoint: number,
  numberOfColumns: number,
): number => {
  const gap = 20;
  const totalPadding = 2 * gap;
  const totalGap = (numberOfColumns - 1) * gap;

  const availableWidth = breakpoint - totalPadding - totalGap;

  const imageWidth = availableWidth / numberOfColumns;

  // Validate imageWidth
  if (isNaN(imageWidth) || imageWidth <= 0) {
    console.warn(
      `Invalid imageWidth calculated: ${imageWidth}. Falling back to default width.`,
    );
    return 300; // Default width in pixels
  }

  return imageWidth;
};

const calculateCappedWidth = (
  breakpoint: number,
  resolutions: number[],
  numberOfColumnsArray: [number, number, number],
): number => {
  const columns =
    breakpoint >= 1280
      ? numberOfColumnsArray[2]
      : breakpoint >= 768
        ? numberOfColumnsArray[1]
        : numberOfColumnsArray[0];

  const validColumns = Number.isInteger(columns) && columns > 0 ? columns : 1;
  const baseWidth = calculateImageWidth(breakpoint, validColumns);
  const maxResolution = Math.max(...resolutions);

  const cappedWidth = Math.min(
    baseWidth,
    Math.floor(MAX_IMAGE_DIMENSION / maxResolution),
  );

  if (isNaN(cappedWidth) || cappedWidth <= 0) {
    console.warn(
      `Invalid cappedWidth calculated: ${cappedWidth}. Falling back to default width.`,
    );
    return 300;
  }

  return cappedWidth;
};

const createSource = (
  imageUrl: string,
  media: string,
  width: number,
  quality: number = 85,
) => (
  <source
    key={media}
    media={media}
    srcSet={`${getImageUrl(imageUrl, width, quality)} 1x, ${getImageUrl(imageUrl, width, quality, 2)} 2x`}
  />
);

const createPreloadLink = (
  imageUrl: string,
  media: string,
  width: number,
  quality: number = 85,
) => (
  <link
    key={`preload-${media}`}
    rel="preload"
    as="image"
    href={getImageUrl(imageUrl, width, quality)}
    media={media}
  />
);

interface ContentfulImageProps
  extends React.HTMLAttributes<HTMLPictureElement> {
  imageUrl: string;
  mobileImageUrl?: string;
  alt: string;
  priority?: boolean;
  small?: boolean;
  isBanner?: boolean;
  icon?: boolean;
  numberOfColumns?: [number, number, number];
}

export const ContentfulImage = forwardRef<
  HTMLPictureElement,
  ContentfulImageProps
>(
  (
    {
      imageUrl,
      priority = false,
      alt,
      small,
      isBanner = false,
      icon = false,
      numberOfColumns = [1, 1, 1],
      className,
      mobileImageUrl,
      ...props
    },
    ref,
  ) => {
    const shouldPreload = priority || isBanner;
    const loading = shouldPreload ? "eager" : "lazy";
    const fetchPriority = isBanner ? "high" : shouldPreload ? "high" : "low";

    if (!imageUrl) return null;
    const renderSources = () => {
      if (icon) {
        return createSource(imageUrl, "(max-width: 9999px)", 50, 100);
      }

      if (small) {
        const width = calculateCappedWidth(600, [1, 2], numberOfColumns);
        return createSource(imageUrl, "(min-width: 768px)", width, 100);
      }

      const mobileImg = mobileImageUrl || imageUrl;
      const sources = [];

      if (isBanner) {
        sources.push(createSource(mobileImg, "(max-width: 767px)", 767));
      } else {
        sources.push(
          createSource(
            mobileImg,
            "(max-width: 375px)",
            calculateCappedWidth(375, [1, 2], numberOfColumns),
          ),
          createSource(
            mobileImg,
            "(max-width: 425px)",
            calculateCappedWidth(425, [1, 2], numberOfColumns),
          ),
          createSource(
            mobileImg,
            "(max-width: 600px)",
            calculateCappedWidth(600, [1, 2], numberOfColumns),
          ),
          createSource(
            mobileImg,
            "(max-width: 767px)",
            calculateCappedWidth(767, [1, 2], numberOfColumns),
          ),
        );
      }

      if (!icon) {
        sources.push(
          createSource(
            imageUrl,
            "(max-width: 1279px)",
            calculateCappedWidth(1279, [1, 2], numberOfColumns),
          ),
          createSource(
            imageUrl,
            "(max-width: 1799px)",
            calculateCappedWidth(1799, [1, 2], numberOfColumns),
            75,
          ),
          createSource(
            imageUrl,
            "(min-width: 1800px)",
            calculateCappedWidth(2560, [1, 2], numberOfColumns),
            75,
          ),
        );
      }

      return sources;
    };

    const renderPreloadLinks = () => {
      if (!shouldPreload) return null;

      return [
        createPreloadLink(
          imageUrl,
          "(max-width: 425px)",
          calculateCappedWidth(425, [1, 2], numberOfColumns),
        ),
        createPreloadLink(
          imageUrl,
          "(min-width: 426px) and (max-width: 767px)",
          calculateCappedWidth(767, [1, 2], numberOfColumns),
        ),
        createPreloadLink(
          imageUrl,
          "(min-width: 768px)",
          calculateCappedWidth(1279, [1, 2], numberOfColumns),
        ),
      ];
    };

    const fallbackWidth = calculateCappedWidth(800, [1, 2], numberOfColumns);

    return (
      <picture
        className={cn("contentful-image h-full w-full", className)}
        ref={ref}
        {...props}
      >
        {renderPreloadLinks()}
        {renderSources()}
        <img
          src={getImageUrl(imageUrl, fallbackWidth)}
          alt={alt}
          className={cn(
            "block h-full w-full object-cover object-center",
            className,
          )}
          loading={loading}
          decoding="async"
          width={200}
          height={230}
          fetchPriority={fetchPriority as any}
        />
      </picture>
    );
  },
);

ContentfulImage.displayName = "ContentfulImage";
